/*
 * Jira Ticket:
 * Created Date: Wed, 18th Mar 2020, 11:57:33 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */
import React, { useState, useEffect } from 'react';
import {
  Create,
  FileField,
  FileInput,
  ReferenceInput,
  required,
  SelectInput,
  NumberInput,
  SimpleForm,
  TextInput,
  useNotify,
  useTranslate,
  useRedirect,
  usePermissions,
  useDataProvider,
  fetchStart,
  fetchEnd,
  Title,
} from 'react-admin';
import { InputAdornment } from '@material-ui/core';
import { useForm } from 'react-final-form';
import { AccessDenied } from 'Layout';
import { columnStyles } from 'styles';
import { onDropRejected } from 'utils';
import { isProjectManager } from 'utils/roles';
import { nonNegativeIntCappedAt20 } from 'utils/validation';
import { usePersistentOrganisation } from '../../Components/Contexts';
import getOrganisationInfo from '../../utils/getOrganisationInfo';
import { useDispatch } from 'react-redux';

const maxFileSize = 2097152; // 2MB in bytes
const allowedFileTypes = 'application/pdf, image/png, image/jpeg';
const platformHeightValidation = [required(), nonNegativeIntCappedAt20];

const SanitizedForm = ({
  basePath,
  classes,
  organisationId,
  organisationName,
  ...props
}) => {
  const form = useForm();
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const projectFilterChanged = event => {
    const currentProjectId = event.target.value;
    if (currentProjectId !== selectedProjectId) {
      form.change('siteId', null);
      setSelectedProjectId(currentProjectId);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <ReferenceInput
          source="projectId"
          reference="Project"
          perPage={1000}
          validate={required()}
          onChange={event => projectFilterChanged(event)}
          filter={{
            organisationId,
            principalOrganisationId: organisationId,
          }}
          label="resources.Tower.fields.project"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
        <NumberInput
          source="platformHeight"
          label="resources.Tower.fields.height"
          customInput={NumberInput}
          validate={platformHeightValidation}
          InputProps={{
            endAdornment: <InputAdornment position="end">m</InputAdornment>,
          }}
        />
      </div>
      <div className={classes.column}>
        {selectedProjectId ? (
          <ReferenceInput
            source="siteId"
            perPage={1000}
            reference="Site"
            validate={required()}
            disabled={!selectedProjectId}
            filter={
              selectedProjectId
                ? {
                    projectId: selectedProjectId,
                  }
                : null
            }
            label="resources.Tower.fields.site"
          >
            <SelectInput optionText="name" />
          </ReferenceInput>
        ) : (
          <TextInput
            source="siteid"
            disabled={true}
            label="resources.Tower.fields.site"
          />
        )}
        <ReferenceInput
          source="towerTypeId"
          reference="TowerType"
          validate={required()}
          allowEmpty={true}
          label="resources.Tower.fields.type"
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
      </div>
    </div>
  );
};

const TowerCreate = props => {
  const { permissions } = usePermissions();
  const { persistentOrganisation } = usePersistentOrganisation();
  const translate = useTranslate();
  const notify = useNotify();
  const dispatch = useDispatch();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const classes = columnStyles();
  let [organisationName, setOrganisationName] = useState('');

  if (!persistentOrganisation) {
    redirect('/');
    notify('error.create.noOrgId', 'warning');
  }

  const useMountEffect = fun => useEffect(fun, []);
  useMountEffect(
    getOrganisationInfo(persistentOrganisation.id, {
      dataProvider,
      dispatch,
      notify,
      setOrganisationName,
    })
  ); // run once

  const divProps = {
    classes,
    organisationId: persistentOrganisation.id,
    organisationName,
    ...props,
  };

  return isProjectManager(permissions) ? (
    <>
      <Title title={`resources.General.cmsTitle`} />
      <Create title={'resources.Tower.fields.create'} {...props}>
        <SimpleForm redirect={`/Tower`}>
          <SanitizedForm {...divProps} />
          <TextInput
            source="notes"
            fullWidth={true}
            multiline={true}
            validate={required()}
            label={'resources.Tower.fields.notes'}
          />
          <FileInput
            source="files"
            maxSize={maxFileSize}
            options={{
              onDropRejected: files =>
                onDropRejected({
                  files,
                  translate,
                  notify,
                  maxFileSize,
                  allowedFileTypes,
                }),
            }}
            multiple={true}
            label={'resources.Tower.fields.mediaUpload'}
            accept={allowedFileTypes}
          >
            <FileField source="src" title="filename" target="_blank" />
          </FileInput>
        </SimpleForm>
      </Create>
    </>
  ) : (
    <AccessDenied />
  );
};

export default TowerCreate;
